import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, MailIcon, PencilIcon, PhoneIcon, PlusIcon } from 'lucide-react';
import { Employee, ProJob, TimeEntry } from '../../types/pro';
import { JOB_STATUS, addDays, formatDate, formatTime, isSameDay, startOfWeek } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
import { EmployeeFormModal } from '../../components/pro/EmployeeFormModal';
interface ProEmployeesProps {
  employees: Employee[];
  jobs: ProJob[];
  timeEntries: TimeEntry[];
  /** Only an administrator may change roles, rates and contact details. */
  isAdmin: boolean;
  onOpenJob: (jobId: string) => void;
  onAdd: (employee: Omit<Employee, 'id'>) => void;
  onUpdate: (id: string, changes: Partial<Employee>) => void;
  onRemove: (id: string) => void;
}
export function ProEmployees({
  employees,
  jobs,
  timeEntries,
  isAdmin,
  onOpenJob,
  onAdd,
  onUpdate,
  onRemove
}: ProEmployeesProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const stats = useMemo(() => {
    const monday = startOfWeek(new Date());
    const sunday = addDays(monday, 7);
    const now = new Date();
    return new Map(employees.map(employee => {
      const week = timeEntries.filter(entry => {
        const date = new Date(entry.date);
        return entry.employeeId === employee.id && date >= monday && date < sunday;
      }).reduce((sum, entry) => sum + entry.hours, 0);
      const own = jobs.filter(job => job.employeeIds.includes(employee.id ?? ''));
      const current = own.find(job => job.status === 'i-gang' && job.start && isSameDay(new Date(job.start), now));
      const todayBooked = own.some(job => job.start && isSameDay(new Date(job.start), now));
      return [employee.id ?? '', {
        week,
        jobs: own,
        status: current ? 'På opgave' : todayBooked ? 'Booket i dag' : 'Ledig',
        statusTone: current ? 'bg-amber-50 text-amber-700' : todayBooked ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'
      }];
    }));
  }, [employees, jobs, timeEntries]);
  const open = employees.find(employee => employee.id === openId);
  if (open) {
    const data = stats.get(open.id ?? '');
    const upcoming = (data?.jobs ?? []).filter(job => job.start && new Date(job.start) >= new Date()).sort((a, b) => (a.start ?? '').localeCompare(b.start ?? ''));
    const hours = timeEntries.filter(entry => entry.employeeId === open.id);
    return <div data-id="element-1342" data-mp="beqel383gfkr">
        <button onClick={() => setOpenId(null)} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1343" data-mp="bdx5hvlnxdzg">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-1344" data-mp="2d8t15bx3loo" />
          Alle medarbejdere
        </button>

        <header className="mb-5 flex items-center gap-4" data-id="element-1345" data-mp="7cbw4k8c7hu0">
          <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full text-lg font-bold text-white" style={{
          backgroundColor: open.color
        }} data-id="element-1346" data-mp="v526wq4i7v2p">
            {open.name.split(' ').map(part => part[0]).join('').slice(0, 2)}
          </span>
          <div className="min-w-0 flex-1" data-id="element-1347" data-mp="35w20r1i0lgax">
            <h1 className="text-2xl font-bold tracking-tight text-stone-900" data-id="element-1348" data-mp="35v0m6n1sibog">
              {open.name}
            </h1>
            <p className="text-sm text-stone-500" data-id="element-1349" data-mp="3awpys74nuxen">
              {open.role}
              {!open.active && ' · inaktiv'}
            </p>
          </div>
          {isAdmin && <button onClick={() => setEditingId(open.id ?? null)} className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1350" data-mp="3ld0jolf0t5pu">
              <PencilIcon className="h-4 w-4" data-id="element-1351" data-mp="cx8lcgogxma5" />
              Rediger
            </button>}
        </header>

        <EmployeeFormModal isOpen={editingId === open.id} onClose={() => setEditingId(null)} employee={open} onSave={changes => open.id && onUpdate(open.id, changes)} onDelete={() => {
        if (open.id) onRemove(open.id);
        setOpenId(null);
      }} data-id="element-1352" data-mp="8x6hc0v9ggrp" />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-id="element-1353" data-mp="2olgacjlyps0y">
          <section className="rounded-xl border border-stone-200 bg-white p-5" data-id="element-1354" data-mp="2jn4yy4g0r9gm">
            <h2 className="mb-3 text-base font-semibold text-stone-900" data-id="element-1355" data-mp="29lu7sp79as80">
              Kontakt
            </h2>
            <ul className="space-y-2.5 text-sm" data-id="element-1356" data-mp="2eknpkttkt5dz">
              <li data-id="element-1357" data-mp="2jiyv4scu8tqe">
                <a href={`tel:${open.phone.replace(/\s/g, '')}`} className="flex items-center gap-2.5 text-stone-700 transition-colors hover:text-brand-700" data-id="element-1358" data-mp="2jifinr5yixjr">
                  <PhoneIcon className="h-4 w-4 text-stone-400" data-id="element-1359" data-mp="2sa8zzftb8cae" />
                  {open.phone}
                </a>
              </li>
              <li data-id="element-1360" data-mp="2x8ixvy6f5qzp">
                <a href={`mailto:${open.email}`} className="flex items-center gap-2.5 break-all text-stone-700 transition-colors hover:text-brand-700" data-id="element-1361" data-mp="2xc6n4l4tzr8m">
                  <MailIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-1362" data-mp="17vfg1mk57rzj" />
                  {open.email}
                </a>
              </li>
              <li className="border-t border-stone-100 pt-2.5" data-id="element-1363" data-mp="1cvbihhd6zbcz">
                <span className="block text-xs uppercase tracking-wide text-stone-400" data-id="element-1364" data-mp="14tkrxgmsyl5p">
                  Timepris
                </span>
                <span className="text-stone-900" data-id="element-1365" data-mp="zw18817c5g6d">{open.hourlyRate} kr/t</span>
              </li>
              <li data-id="element-1366" data-mp="1esrztoarpnlf">
                <span className="block text-xs uppercase tracking-wide text-stone-400" data-id="element-1367" data-mp="1etbkvdvpss3o">
                  Timer denne uge
                </span>
                <span className="text-stone-900" data-id="element-1368" data-mp="1ns6pilj41u4v">
                  {(data?.week ?? 0).toLocaleString('da-DK')} t
                </span>
              </li>
            </ul>
          </section>

          <section className="overflow-hidden rounded-xl border border-stone-200 bg-white lg:col-span-2" data-id="element-1369" data-mp="1xi8dldbgrcjs">
            <header className="border-b border-stone-100 px-5 py-3.5" data-id="element-1370" data-mp="1sivhs32m6v52">
              <h2 className="text-base font-semibold text-stone-900" data-id="element-1371" data-mp="1slrghtmadnuk">
                Kommende opgaver
              </h2>
            </header>
            {upcoming.length === 0 ? <EmptyState title="Ingen kommende opgaver" data-id="element-1372" data-mp="22jfjt7xyk3oc" /> : <ul className="divide-y divide-stone-100" data-id="element-1373" data-mp="2l76l9ydih70e">
                {upcoming.map(job => <li key={job.id} data-id="element-1374" data-mp="2q3okyjtzif5v">
                    <button onClick={() => job.id && onOpenJob(job.id)} className="flex w-full items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-stone-50" data-id="element-1375" data-mp="2q2lxklvveuup">
                      <span className="min-w-0 flex-1" data-id="element-1376" data-mp="22f5d25euk4eb">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1377" data-mp="22dkiwgwg2ngz">
                          {job.title}
                        </span>
                        <span className="block text-xs text-stone-500" data-id="element-1378" data-mp="1tg0ex0776d2o">
                          {formatDate(job.start ?? '')}{' '}
                          {formatTime(job.start ?? '')} · {job.customerName}
                        </span>
                      </span>
                      <StatusChip meta={JOB_STATUS[job.status]} data-id="element-1379" data-mp="1og5pl132lvjq" />
                    </button>
                  </li>)}
              </ul>}
          </section>

          <section className="overflow-hidden rounded-xl border border-stone-200 bg-white lg:col-span-3" data-id="element-1380" data-mp="1yysr9p7gc86e">
            <header className="border-b border-stone-100 px-5 py-3.5" data-id="element-1381" data-mp="1uwfy009w62qi">
              <h2 className="text-base font-semibold text-stone-900" data-id="element-1382" data-mp="1uysf6b7ts92i">
                Registrerede timer
              </h2>
            </header>
            {hours.length === 0 ? <EmptyState title="Ingen registrerede timer" data-id="element-1383" data-mp="1l02uybsgicj3" /> : <ul className="divide-y divide-stone-100" data-id="element-1384" data-mp="1g79qzq4tb882">
                {hours.map(entry => {
              const job = jobs.find(j => j.id === entry.jobId);
              return <li key={entry.id} className="flex items-center gap-4 px-5 py-3" data-id="element-1385" data-mp="178o81tvfhr6r">
                      <span className="min-w-0 flex-1 truncate text-sm text-stone-900" data-id="element-1386" data-mp="x979rxiiafkt">
                        {job?.title ?? 'Opgave'}
                      </span>
                      <span className="text-xs text-stone-500" data-id="element-1387" data-mp="38war95z8f4b1">
                        {entry.workType} · {formatDate(entry.date)}
                      </span>
                      <span className="w-16 text-right text-sm font-semibold tabular-nums text-stone-900" data-id="element-1388" data-mp="3iugmj4qj9c6x">
                        {entry.hours.toLocaleString('da-DK')} t
                      </span>
                    </li>;
            })}
              </ul>}
          </section>
        </div>
      </div>;
  }
  return <div data-id="element-1389" data-mp="2v2rr3fif07lr">
      <PageHeader title="Medarbejdere" subtitle={isAdmin ? `${employees.filter(e => e.active).length} aktive — klik en række for at redigere` : `${employees.filter(e => e.active).length} aktive`} actions={isAdmin && <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1391" data-mp="2bgwstaztf7s8">
              <PlusIcon className="h-4 w-4" data-id="element-1392" data-mp="b0nfhdqm2r7h" />
              Ny medarbejder
            </button>} data-id="element-1390" data-mp="2z6r77wzs94el" />

      <EmployeeFormModal isOpen={creating} onClose={() => setCreating(false)} onSave={onAdd} data-id="element-1393" data-mp="f1h09y8rbpw5" />

      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white" data-id="element-1394" data-mp="3jhrt59jvuc6o">
        <table className="w-full min-w-[680px] text-sm" data-id="element-1395" data-mp="3jfyp820nm62d">
          <thead data-id="element-1396" data-mp="3nk6eq1exrq1n">
            <tr className="border-b border-stone-200 bg-stone-50 text-left" data-id="element-1397" data-mp="3njmx6lt773o5">
              {['Navn', 'Rolle', 'Telefon', 'E-mail', 'Status', 'Timer denne uge'].map(heading => <th key={heading} scope="col" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1398" data-mp="2zt9azbxanz1h">
                  {heading}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100" data-id="element-1399" data-mp="2sb2t9stxq6te">
            {employees.map(employee => {
            const data = stats.get(employee.id ?? '');
            return <tr key={employee.id} onClick={() => setOpenId(employee.id ?? null)} className="cursor-pointer transition-colors hover:bg-stone-50" data-id="element-1400" data-mp="2j8tl39m52pj6">
                  <td className="px-4 py-3" data-id="element-1401" data-mp="29ih2hmxvx6fo">
                    <span className="flex items-center gap-2.5" data-id="element-1402" data-mp="24k5ttfjbt1z1">
                      <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{
                    backgroundColor: employee.color
                  }} data-id="element-1403" data-mp="24ick1rdf21mt" />
                      <span className="font-medium text-stone-900" data-id="element-1404" data-mp="1vm3hi3u56mwg">
                        {employee.name}
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-stone-600" data-id="element-1405" data-mp="1gw0hz0ep8cls">{employee.role}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-stone-600" data-id="element-1406" data-mp="1kzosnrymnob9">
                    {employee.phone}
                  </td>
                  <td className="max-w-[200px] truncate px-4 py-3 text-stone-600" data-id="element-1407" data-mp="171gytsww134b">
                    {employee.email}
                  </td>
                  <td className="px-4 py-3" data-id="element-1408" data-mp="1znjlr9gjruov">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${data?.statusTone}`} data-id="element-1409" data-mp="1zpwcab0dfddj">
                      {data?.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold tabular-nums text-stone-900" data-id="element-1410" data-mp="1uwbccdf7w9bp">
                    {(data?.week ?? 0).toLocaleString('da-DK')} t
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}